// @TODO: add concrete type for `currentActiveLayer`
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";

interface MapState {
  initialMapView: {
    longitude: number;
    latitude: number;
    zoom: number;
    pitch: number;
  };
  mapViewingMode: "3d" | "2d";
  currentActiveLayer: Record<string, unknown> | undefined;
  showLayerPanel: boolean;
  hasMapInitialize: boolean;
  showOnlySelectedEndpointsDataOnMap: boolean;
  /** Viewport bbox string "minLng,minLat,maxLng,maxLat" updated on map moveend. */
  cmTableBbox: string | null;
  /** True while a CM Table GeoJSON fetch is in progress. */
  isCmTableLoading: boolean;
  /** True during the debounce period after map move, before bbox is committed. */
  isMapBboxDebouncing: boolean;
  /** True while fetching spatial dataset detail after drawer selection. */
  isSpatialDatasetLoading: boolean;
  /** Incremented after a CM table row edit to force useCmTableMapLayers to re-fetch. */
  cmTableLayerVersion: number;
  /** Incremented after a spatial dataset upload completes to force a list refresh. */
  spatialDatasetVersion: number;
  /**
   * Entity ID of the spatial dataset currently loaded as the active map overlay.
   * Unlike selectedEntityId (cleared on endpoint switch), this persists as long as
   * the layer is visible so clicking the raster/vector layer can always restore the
   * full selection even after the user has navigated to a different table.
   */
  activeSpatialDatasetEntityId: number | string | null;
  /** Pending coordinate fly-to triggered by the search bar. MapComponent consumes and clears this. */
  pendingFlyTo: { lng: number; lat: number; zoom: number } | null;
  /** Pin marker placed on the map after a coordinate search. Dismissed on click. */
  coordinateSearchMarker: { lng: number; lat: number } | null;

  // Separate namespace for store actions
  actions: {
    setInitialMapView: (payload: MapState["initialMapView"]) => void;
    increaseZoomLevel: (level?: number) => void;
    decreaseZoomLevel: (level?: number) => void;
    setZoomLevel: (level: number) => void;
    setMapViewingMode: (mode: MapState["mapViewingMode"]) => void;
    setCurrentActiveLayer: (layer: Record<string, unknown>) => void;
    setCommonMapState: (paylod: Partial<Omit<MapState, "actions">>) => void;
    toggleLayerPanel: () => void;
    setShowOnlySelectedEndpointsDataOnMap: (show: boolean) => void;
    setCmTableBbox: (bbox: string | null) => void;
    setIsCmTableLoading: (loading: boolean) => void;
    setIsMapBboxDebouncing: (debouncing: boolean) => void;
    bumpCmTableLayerVersion: () => void;
    bumpSpatialDatasetVersion: () => void;
    setPendingFlyTo: (target: { lng: number; lat: number; zoom: number } | null) => void;
    setCoordinateSearchMarker: (coords: { lng: number; lat: number } | null) => void;
    resetCommonMapState: () => void;
  };
}

const actionName = actionNameBuilder("map");

export const useMapStore = create<MapState>()(
  devtools(
    immer(
      applyResetMiddleware((set, _, store) => ({
        // Default map view state
        // NOTE: these coordinates [0, 0] are placeholder values, should overwritten by `config`
        // Actual values should be set once `config` available
        initialMapView: {
          longitude: 0,
          latitude: 0,
          zoom: 8,
          pitch: 0
        },
        mapViewingMode: "2d",
        currentActiveLayer: undefined,
        showLayerPanel: false,
        hasMapInitialize: false,
        showOnlySelectedEndpointsDataOnMap: false,
        cmTableBbox: null,
        isCmTableLoading: false,
        isMapBboxDebouncing: false,
        isSpatialDatasetLoading: false,
        cmTableLayerVersion: 0,
        spatialDatasetVersion: 0,
        activeSpatialDatasetEntityId: null,
        pendingFlyTo: null,
        coordinateSearchMarker: null,

        actions: {
          setInitialMapView: (payload) => set(() => ({ initialMapView: payload })),
          increaseZoomLevel: (payload) =>
            set(
              (state) => {
                if ((payload && payload > 22) || state.initialMapView?.zoom > 22) return;
                state.initialMapView.zoom += 1;
              },
              undefined,
              actionName("increaseZoomLevel")
            ),

          decreaseZoomLevel: (payload) =>
            set(
              (state) => {
                if ((payload && payload < 0) || state.initialMapView?.zoom < 0) return;
                state.initialMapView.zoom -= 1;
              },
              undefined,
              actionName("decreaseZoomLevel")
            ),

          setZoomLevel: (payload) =>
            set(
              (state) => {
                if (payload < 0 || payload > 22) return;
                state.initialMapView.zoom = payload;
              },
              undefined,
              actionName("setZoomLevel")
            ),

          setMapViewingMode: (payload) => set(() => ({ mapViewingMode: payload }), undefined, actionName("setMapViewingMode")),

          setCurrentActiveLayer: (payload) => set(() => ({ currentActiveLayer: payload }), undefined, actionName("setCurrentActiveLayer")),

          toggleLayerPanel: () =>
            set(
              (state) => {
                state.showLayerPanel = !state.showLayerPanel;
              },
              undefined,
              actionName("toggleLayerPanel")
            ),

          setCommonMapState: (payload) => set(() => ({ ...payload }), undefined, actionName("setCommonMapState")),

          setShowOnlySelectedEndpointsDataOnMap: (show) =>
            set(
              (state) => {
                state.showOnlySelectedEndpointsDataOnMap = show;
              },
              undefined,
              actionName("setShowOnlySelectedOnMap")
            ),

          setCmTableBbox: (bbox) =>
            set(
              (state) => {
                state.cmTableBbox = bbox;
              },
              undefined,
              actionName("setCmTableBbox")
            ),

          setIsCmTableLoading: (loading) =>
            set(
              (state) => {
                state.isCmTableLoading = loading;
              },
              undefined,
              actionName("setIsCmTableLoading")
            ),

          setIsMapBboxDebouncing: (debouncing) =>
            set(
              (state) => {
                state.isMapBboxDebouncing = debouncing;
              },
              undefined,
              actionName("setIsMapBboxDebouncing")
            ),

          bumpCmTableLayerVersion: () =>
            set(
              (state) => {
                state.cmTableLayerVersion += 1;
              },
              undefined,
              actionName("bumpCmTableLayerVersion")
            ),

          bumpSpatialDatasetVersion: () =>
            set(
              (state) => {
                state.spatialDatasetVersion += 1;
              },
              undefined,
              actionName("bumpSpatialDatasetVersion")
            ),

          setPendingFlyTo: (target) =>
            set(
              (state) => {
                state.pendingFlyTo = target;
              },
              undefined,
              actionName("setPendingFlyTo")
            ),

          setCoordinateSearchMarker: (coords) =>
            set(
              (state) => {
                state.coordinateSearchMarker = coords;
              },
              undefined,
              actionName("setCoordinateSearchMarker")
            ),

          resetCommonMapState: () => set(store.getInitialState(), undefined, actionName("resetCommonMapState"))
        }
      }))
    ),
    {
      name: "mapStore"
    }
  )
);

// Hook for store states
export const useMapSelector = () =>
  useMapStore(
    useShallow((state) => ({
      initialMapView: state.initialMapView,
      mapViewingMode: state.mapViewingMode,
      currentActiveLayer: state.currentActiveLayer,
      showLayerPanel: state.showLayerPanel,
      hasMapInitialize: state.hasMapInitialize,
      showOnlySelectedEndpointsDataOnMap: state.showOnlySelectedEndpointsDataOnMap,
      cmTableBbox: state.cmTableBbox,
      isCmTableLoading: state.isCmTableLoading,
      isMapBboxDebouncing: state.isMapBboxDebouncing,
      isSpatialDatasetLoading: state.isSpatialDatasetLoading,
      cmTableLayerVersion: state.cmTableLayerVersion,
      spatialDatasetVersion: state.spatialDatasetVersion,
      pendingFlyTo: state.pendingFlyTo,
      coordinateSearchMarker: state.coordinateSearchMarker
    }))
  );

// Hook for store actions
export const useMapActions = () => useMapStore((state) => state.actions);
